import React, { useEffect, useRef, useState } from 'react';
import { Save, Check, Trash2, Upload, Globe, Plus, CheckCircle2, FolderOpen, Table2, BarChart3, Copy } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import FileAnalyticsAPI from '../file_analytics/fileAnalyticsService';
import { AuthenticatorRecord, CollectionType, DataSource } from '../file_analytics/types';
import { isTrustedMessageOrigin } from '../../utils/trustedOrigins';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import AuthenticatorModal from '../file_analytics/modals/AuthenticatorModal';
import CombineDataPanel, { CombineConfig, CombineJoinKey, mkCombineConfig, mkJoinKey, toCombineLinks } from '../file_analytics/modals/CombineDataPanel';

// The five data-source extensions a pipeline allow-list can contain (R11).
const ALLOW_EXTS = ['pdf', 'xlsx', 'xls', 'csv', 'txt'] as const;

type SourceKind = 'normal' | 'api' | 'graph' | 'combined' | 'aggregation';
type CollectionMode = 'accumulated' | 'incremental';
const KIND_TABS: { value: SourceKind; label: string; Icon: React.ElementType; desc: string }[] = [
  { value: 'normal',      label: 'File Upload',       Icon: Upload,    desc: 'Upload CSV, Excel or other flat files'         },
  { value: 'api',         label: 'API',               Icon: Globe,     desc: 'Pull data from any REST / JSON API'            },
  { value: 'graph',       label: 'Microsoft Graph',   Icon: FolderOpen,desc: 'Read files from SharePoint / OneDrive folders' },
  { value: 'combined',    label: 'Combined Dataset',  Icon: Table2,    desc: 'Join two existing sources into one dataset'    },
  { value: 'aggregation', label: 'Aggregation',       Icon: BarChart3, desc: 'Group-by summary computed from another source' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (ct: CollectionType) => void;
  onSourceSaved?: (ds: DataSource, openLinks?: boolean) => void;
  /** When editing, a Delete button appears in the footer and calls this. */
  onDelete?: () => void;
  /** null/undefined → create; set → edit (pre-fill). Pipeline mode only. */
  pipeline?: CollectionType | null;
  /** Data source to edit (data_source mode only). */
  sourceToEdit?: DataSource | null;
  /** Title noun — "Pipeline" (rules section) or "Data Source" (data-sources section). */
  noun?: string;
  mode?: 'pipeline' | 'data_source';
  /** Called when user picks Aggregation type — parent mounts AggregationBuilderModal. */
  onOpenAggregationBuilder?: () => void;
}

const CmPipelineEditorModal: React.FC<Props> = ({
  isOpen, onClose, onSaved, onSourceSaved, onDelete,
  pipeline, sourceToEdit, noun = 'Pipeline', mode = 'pipeline',
  onOpenAggregationBuilder,
}) => {
  const isDs = mode === 'data_source';
  const isEdit = isDs ? !!sourceToEdit : !!pipeline;
  const fieldVh = useFieldValidation();
  const { allowed: canModify, loading: permLoading } = usePermission('CMG_RULES', 'view+modify');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconKey, setIconKey] = useState('custom');
  const [colorKey, setColorKey] = useState('indigo');
  const [allowed, setAllowed] = useState<Record<string, boolean>>(
    Object.fromEntries(ALLOW_EXTS.map(e => [e, true])),
  );
  // Source type + API fields
  const [sourceKind, setSourceKind] = useState<SourceKind>('normal');
  const [apiUrl, setApiUrl] = useState('');
  const [collectionMode, setCollectionMode] = useState<CollectionMode>('accumulated');
  const [authenticatorId, setAuthenticatorId] = useState('');
  const [authenticators, setAuthenticators] = useState<AuthenticatorRecord[]>([]);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  // Microsoft Graph — folder path + post-creation OAuth connect step
  const [graphFolder, setGraphFolder] = useState('');
  const [pendingGraphSource, setPendingGraphSource] = useState<DataSource | null>(null);
  const [graphConnecting, setGraphConnecting] = useState(false);
  const [graphConnected, setGraphConnected] = useState(false);
  const graphMsgListenerRef = useRef<((ev: MessageEvent) => void) | null>(null);
  // Combined dataset — flat join config
  const [allSources, setAllSources] = useState<DataSource[]>([]);
  const [combineConfig, setCombineConfig] = useState<CombineConfig>(mkCombineConfig());
  const [submitting, setSubmitting] = useState(false);
  // Clone mode — data_source create only
  const [cloneMode, setCloneMode] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState('');

  const patchCombine = (patch: Partial<CombineConfig>) =>
    setCombineConfig(prev => ({ ...prev, ...patch }));

  const patchJoinKey = (ki: number, patch: Partial<CombineJoinKey>) =>
    setCombineConfig(prev => ({
      ...prev,
      joinKeys: prev.joinKeys.map((k, i) => i === ki ? { ...k, ...patch } : k),
    }));

  const [toast, setToast] = useState<ToastMessage | null>(null);

  const loadAuthenticators = async () => {
    try { setAuthenticators(await FileAnalyticsAPI.listAuthenticators()); } catch { /* non-fatal */ }
  };

  const loadSources = async () => {
    try { setAllSources(await FileAnalyticsAPI.listSources()); } catch { /* non-fatal */ }
  };

  // (Re)hydrate form whenever modal opens or target changes.
  useEffect(() => {
    if (!isOpen) return;
    fieldVh.reset();
    setSubmitting(false);
    setCloneMode(false);
    setCloneSourceId('');
    setPendingGraphSource(null);
    setGraphConnecting(false);
    setGraphConnected(false);
    setCombineConfig(mkCombineConfig());
    const item = isDs ? sourceToEdit : pipeline;
    if (item) {
      setName(item.name ?? '');
      setDescription(item.description ?? '');
      setIconKey(item.icon_key ?? 'custom');
      setColorKey(item.color_key ?? 'indigo');
      const list = item.allowed_source_types;
      setAllowed(Object.fromEntries(ALLOW_EXTS.map(e => [e, Array.isArray(list) ? list.includes(e) : true])));
      if (isDs) {
        const ds = item as DataSource;
        const rawKind = ds.source_type ?? 'normal';
        const kind: SourceKind = (['normal','api','graph','combined','aggregation'] as SourceKind[]).includes(rawKind as SourceKind)
          ? (rawKind as SourceKind) : 'normal';
        setSourceKind(kind);
        setApiUrl(ds.api_endpoint ?? '');
        setCollectionMode((ds.collection_mode as CollectionMode) ?? 'accumulated');
        setAuthenticatorId(ds.authenticator_id ?? '');
        if (kind === 'graph') setGraphFolder(ds.api_endpoint ?? '');
        if (kind === 'combined') {
          loadSources();
          if (sourceToEdit) {
            // Reload existing combine links — link[0]=left, link[1]=right
            FileAnalyticsAPI.listSourceLinks(Number(sourceToEdit.id)).then(links => {
              const l0 = links.find((l: any) => l.sort_order === 0) ?? links[0];
              const l1 = links.find((l: any) => l.sort_order === 1) ?? links[1];
              if (l0 && l1) {
                setCombineConfig({
                  leftSourceId:  String(l0.ref_source_id),
                  rightSourceId: String(l1.ref_source_id),
                  joinType: l0.join_type ?? 'left',
                  joinKeys: (l0.join_keys ?? []).map((k: any) => ({
                    tempId: `t${Math.random().toString(36).slice(2,9)}`,
                    left_col: k.left_col, right_col: k.right_col,
                  })),
                  leftColumns:  l0.selected_columns ?? [],
                  rightColumns: l1.selected_columns ?? [],
                });
              } else {
                setCombineConfig(mkCombineConfig());
              }
            }).catch(() => setCombineConfig(mkCombineConfig()));
          } else {
            setCombineConfig(mkCombineConfig());
          }
        }
        if (kind === 'api') loadAuthenticators();
      }
    } else {
      setName(''); setDescription(''); setIconKey('custom'); setColorKey('indigo');
      setAllowed(Object.fromEntries(ALLOW_EXTS.map(e => [e, isDs ? e !== 'pdf' : true])));
      setSourceKind('normal'); setApiUrl(''); setCollectionMode('accumulated');
      setAuthenticatorId('');
      setGraphFolder('');
      setCombineConfig(mkCombineConfig());
    }
  }, [isOpen, isDs, pipeline, sourceToEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const allowedList = ALLOW_EXTS.filter(e => allowed[e]);
  const isApi   = isDs && sourceKind === 'api';
  const isGraph = isDs && sourceKind === 'graph';

  // Aggregation delegates to the builder — no name/source required here.
  const canOpenBuilder = isDs && sourceKind === 'aggregation' && !submitting;
  const canSubmit = !submitting && (
    isDs && cloneMode
      ? name.trim().length > 0 && !!cloneSourceId
    : canOpenBuilder ? true
    : name.trim().length > 0 && (
      !isDs                     ? allowedList.length > 0
      : sourceKind === 'normal' ? allowedList.length > 0
      : sourceKind === 'api'    ? apiUrl.startsWith('https://') && !!authenticatorId
      : sourceKind === 'graph'  ? (sourceToEdit ? !!graphFolder.trim() : graphConnected && !!graphFolder.trim())
      : true  // combined: name only
    )
  );

  const connectGraphAccount = async (source: DataSource) => {
    setGraphConnecting(true);
    try {
      const authorizeUrl = await FileAnalyticsAPI.getGraphAuthorizeUrl(Number(source.id));
      const popup = window.open(authorizeUrl, 'ms_graph_auth', 'width=520,height=620,left=200,top=100');
      if (!popup) {
        setToast({ type: 'error', message: 'Could not open popup. Allow popups for this page.' });
        setGraphConnecting(false);
        return;
      }
      const onMsg = (ev: MessageEvent) => {
        // ANYGRC_Vulnerabilities: "postMessage Origin Not Validated in OAuth
        // Popup Handlers" — reject messages from an untrusted origin before
        // acting on the payload.
        if (!isTrustedMessageOrigin(ev.origin)) return;
        if (ev.data?.type !== 'graph_oauth') return;
        window.removeEventListener('message', onMsg);
        graphMsgListenerRef.current = null;
        popup.close();
        setGraphConnecting(false);
        if (ev.data.success) {
          setGraphConnected(true);
        } else {
          setToast({ type: 'error', message: 'Microsoft account connection failed. Try again.' });
        }
      };
      graphMsgListenerRef.current = onMsg;
      window.addEventListener('message', onMsg);
    } catch {
      setToast({ type: 'error', message: 'Failed to start Microsoft login.' });
      setGraphConnecting(false);
    }
  };

  const handleGraphConnectInForm = async () => {
    if (!name.trim()) {
      setToast({ type: 'error', message: 'Enter a name before connecting.' });
      return;
    }
    let source = pendingGraphSource;
    if (!source) {
      setSubmitting(true);
      try {
        source = await FileAnalyticsAPI.createSource({
          name: name.trim(), description: description.trim(),
          icon_key: iconKey, color_key: colorKey,
          source_type: 'graph', api_endpoint: '', collection_mode: collectionMode,
        });
        setPendingGraphSource(source);
      } catch (err: any) {
        setToast({ type: 'error', message: err?.response?.data?.detail || 'Create failed.' });
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    }
    await connectGraphAccount(source);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
    // Aggregation: delegate to builder before any validation — no source created here.
    if (canOpenBuilder && !sourceToEdit) {
      onClose();
      onOpenAggregationBuilder?.();
      return;
    }

    const valid = fieldVh.validateAll([
      { name: 'name', code: 109, value: name, required: true },
      { name: 'description', code: 110, value: description },
    ]);
    if (!valid) return;
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Clone mode: call the clone endpoint and hand back the result.
      if (isDs && cloneMode) {
        const cloned = await FileAnalyticsAPI.cloneSource(cloneSourceId, name.trim());
        onSourceSaved?.(cloned, false);
        return;
      }
      const allowed_source_types = [...allowedList];
      if (isDs) {
        const isCombined = sourceKind === 'combined';
        const backendSourceType = isCombined ? 'normal' : sourceKind;
        const payload: any = {
          name: name.trim(), description: description.trim(),
          icon_key: iconKey, color_key: colorKey,
          source_type: backendSourceType,
        };
        if (isGraph) {
          payload.api_endpoint = graphFolder.trim();
          payload.collection_mode = collectionMode;
        } else if (isApi) {
          if (apiUrl.trim()) payload.api_endpoint = apiUrl.trim();
          payload.collection_mode = collectionMode;
          if (authenticatorId) payload.authenticator_id = authenticatorId;
        } else if (backendSourceType === 'normal') {
          payload.allowed_source_types = isCombined ? [] : allowed_source_types;
        }
        // Graph create: source was pre-created during auth; just update folder path.
        if (isGraph && !sourceToEdit) {
          if (!pendingGraphSource) {
            setToast({ type: 'error', message: 'Connect Microsoft Account first.' });
            setSubmitting(false);
            return;
          }
          const updated = await FileAnalyticsAPI.updateSource(pendingGraphSource.id, {
            name: name.trim(), description: description.trim(),
            icon_key: iconKey, color_key: colorKey,
            api_endpoint: graphFolder.trim(),
            collection_mode: collectionMode,
          } as any);
          onSourceSaved?.(updated, false);
          return;
        }
        const saved = sourceToEdit
          ? await FileAnalyticsAPI.updateSource(sourceToEdit.id, payload)
          : await FileAnalyticsAPI.createSource(payload);
        // Combined: write / replace the two join links (left + right).
        if (isCombined && combineConfig.leftSourceId && combineConfig.rightSourceId) {
          if (sourceToEdit) {
            const existing = await FileAnalyticsAPI.listSourceLinks(Number(saved.id));
            await Promise.all(existing.map((lnk: any) => FileAnalyticsAPI.deleteSourceLink(Number(saved.id), lnk.id).catch(() => {})));
          }
          const links = toCombineLinks(combineConfig);
          await Promise.all(links.map(lnk => FileAnalyticsAPI.createSourceLink(Number(saved.id), lnk)));
        }
        onSourceSaved?.(saved, false);
      } else {
        let saved: CollectionType;
        if (pipeline) {
          saved = await FileAnalyticsAPI.updateCollectionType(pipeline.id, {
            name: name.trim(), description: description.trim(),
            icon_key: iconKey, color_key: colorKey, allowed_source_types,
          });
        } else {
          const created = await FileAnalyticsAPI.createCollectionType({
            name: name.trim(), description: description.trim(),
            icon_key: iconKey, color_key: colorKey,
          });
          saved = await FileAnalyticsAPI.updateCollectionType(created.id, { allowed_source_types });
        }
        onSaved(saved);
      }
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || (isEdit ? 'Update failed' : 'Create failed') });
      setSubmitting(false);
    }
  };

  return (
    <>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <Modal isOpen={isOpen} onClose={submitting ? () => {} : onClose} size="md">
        <Modal.Header>{isEdit ? `Edit ${noun}` : `Create ${noun}`}</Modal.Header>
        <Modal.Body>
            <form id="cm-pipeline-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4" data-testid="MOD_cm_pipeline_form">
          <ValidatedInput
            vh={fieldVh} name="name" code={109} required label="Name"
            value={name} onChange={e => setName(e.target.value)}
            disabled={submitting} placeholder="e.g. Vendor Invoices"
            data-testid="INP_cm_pipeline_name"
          />
          <ValidatedTextarea
            vh={fieldVh} name="description" code={110} label="Description" rows={2}
            value={description} onChange={e => setDescription(e.target.value)}
            disabled={submitting} placeholder="Optional context"
            data-testid="INP_cm_pipeline_description"
          />

          {/* Source type selector — data_source mode only */}
          {isDs && (
            <>
              {/* Clone mode toggle — create only */}
              {!isEdit && (
                <div style={{ display: 'flex', gap: 8, marginBottom: -4 }}>
                  <button
                    type="button"
                    onClick={() => { setCloneMode(false); }}
                    style={{
                      flex: 1, padding: '7px 0', fontSize: 12, fontWeight: 600, borderRadius: 6,
                      border: `1.5px solid ${!cloneMode ? 'var(--primary, #4f46e5)' : 'var(--border-subtle, #e5e7eb)'}`,
                      background: !cloneMode ? 'var(--primary-50, #eef2ff)' : 'transparent',
                      color: !cloneMode ? 'var(--primary, #4f46e5)' : 'var(--text-muted, #6b7280)',
                      cursor: 'pointer',
                    }}
                  >
                    New source
                  </button>
                  <button
                    type="button"
                    onClick={() => { setCloneMode(true); loadSources(); setCloneSourceId(''); }}
                    style={{
                      flex: 1, padding: '7px 0', fontSize: 12, fontWeight: 600, borderRadius: 6,
                      border: `1.5px solid ${cloneMode ? 'var(--primary, #4f46e5)' : 'var(--border-subtle, #e5e7eb)'}`,
                      background: cloneMode ? 'var(--primary-50, #eef2ff)' : 'transparent',
                      color: cloneMode ? 'var(--primary, #4f46e5)' : 'var(--text-muted, #6b7280)',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    <Copy size={13} /> Clone existing
                  </button>
                </div>
              )}

              {/* Clone form */}
              {cloneMode && !isEdit && (
                <>
                  <FormControl.Select
                    label="Source to clone"
                    required
                    value={cloneSourceId}
                    onChange={e => {
                      const id = e.target.value;
                      setCloneSourceId(id);
                      const src = allSources.find(s => String(s.id) === id);
                      if (src && !name.trim()) setName(src.name + ' (copy)');
                    }}
                    disabled={submitting}
                  >
                    <option value="">— Select a source —</option>
                    {allSources.map(s => (
                      <option key={s.id} value={String(s.id)}>{s.name}</option>
                    ))}
                  </FormControl.Select>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -8, lineHeight: 1.5 }}>
                    Copies column schema, clean recipe, and enrich/combine links. No data or job history is copied.
                  </div>
                </>
              )}

              {/* Normal source type card grid — hidden in clone mode */}
              {!cloneMode && (<>
              <div>
                <label className="fi-label" style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-heading)', display: 'block', marginBottom: 8 }}>
                  Source Type
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
                  {KIND_TABS.map(({ value, label, Icon }) => {
                    const active = sourceKind === value;
                    return (
                      <button
                        key={value} type="button"
                        disabled={submitting || !!sourceToEdit}
                        onClick={() => {
                          setSourceKind(value);
                          if (value === 'api') loadAuthenticators();
                          if (value === 'combined') { loadSources(); setCombineConfig(mkCombineConfig()); }
                          if (value === 'graph') { setGraphFolder(''); setGraphConnected(false); setPendingGraphSource(null); }
                        }}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                          padding: '12px 8px', borderRadius: 10,
                          border: `2px solid ${active ? 'var(--primary, #4f46e5)' : 'var(--border-color, #e5e7eb)'}`,
                          background: active ? 'var(--primary-50, #eef2ff)' : 'var(--bg-card, #fff)',
                          color: active ? 'var(--primary, #4f46e5)' : 'var(--text-muted, #6b7280)',
                          cursor: (submitting || !!sourceToEdit) ? 'not-allowed' : 'pointer',
                          opacity: (submitting || !!sourceToEdit) ? 0.6 : 1,
                          transition: 'border-color .15s, background .15s, color .15s',
                          textAlign: 'center',
                        }}
                      >
                        <Icon size={20} strokeWidth={active ? 2.2 : 1.6} />
                        <span style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2 }}>{label}</span>
                      </button>
                    );
                  })}
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', marginTop: 6 }}>
                  {KIND_TABS.find(t => t.value === sourceKind)?.desc}
                </p>
              </div>

              {/* File upload — accepted extensions allow-list */}
              {sourceKind === 'normal' && (
                <div className="cm-pipeline-allowlist">
                  <label className="fi-label" style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-heading)' }}>
                    Accepted File Types
                  </label>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
                    {ALLOW_EXTS.map(ext => (
                      <span key={ext} data-testid={`CHK_cm_pipeline_allow_${ext}`}>
                        <FormControl.Checkbox
                          label={`.${ext}`} checked={!!allowed[ext]}
                          onChange={checked => setAllowed(prev => ({ ...prev, [ext]: checked }))}
                          disabled={submitting} noMargin
                        />
                      </span>
                    ))}
                  </div>
                  {allowedList.length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--red-600)', marginTop: 6 }}>Select at least one file type.</div>
                  )}
                </div>
              )}

              {/* Section 1 header — shown for all non-file-upload source types */}
              {sourceKind !== 'normal' && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  borderTop: '1px solid var(--border-color, #e5e7eb)', paddingTop: 12, marginTop: 4,
                }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%', background: 'var(--primary, #4f46e5)',
                    color: '#fff', fontSize: 12, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>I</span>
                  <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-heading)' }}>
                    {sourceKind === 'api' ? 'API Connection'
                    : sourceKind === 'graph' ? 'OneDrive / SharePoint Folder'
                    : sourceKind === 'combined' ? 'Join Configuration'
                    : 'Aggregation Builder'}
                  </span>
                </div>
              )}

              {/* API source */}
              {sourceKind === 'api' && (
                <>
                  <FormControl.Input
                    label="Data URL" required value={apiUrl}
                    onChange={e => setApiUrl(e.target.value)}
                    placeholder="https://api.example.com/data" disabled={submitting}
                  />
                  <fieldset className="field-group">
                    <legend className="field-group-legend">Authentication</legend>
                    <div className="field-group-body">
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                          <FormControl.Select
                            label="Authenticator" value={authenticatorId}
                            onChange={e => setAuthenticatorId(e.target.value)} disabled={submitting}
                          >
                            <option value="">— Select authenticator —</option>
                            {authenticators.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                          </FormControl.Select>
                        </div>
                        <Button variant="outline" icon={Plus} onClick={() => setAuthModalOpen(true)}
                          disabled={submitting} type="button" style={{ flexShrink: 0, marginBottom: 2 }}>
                          New
                        </Button>
                      </div>
                      {(() => {
                        const sel = authenticators.find(a => a.id === authenticatorId);
                        if (!sel?.session_valid) return null;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--green-700, #15803d)' }}>
                            <CheckCircle2 size={13} />
                            {`Connected · last tested ${sel.last_tested_at ? new Date(sel.last_tested_at).toLocaleString() : 'unknown'}`}
                          </div>
                        );
                      })()}
                    </div>
                  </fieldset>
                  <fieldset className="field-group">
                    <legend className="field-group-legend">Data Collection</legend>
                    <div className="field-group-body">
                      <div className="flex gap-6">
                        {(['accumulated', 'incremental'] as CollectionMode[]).map(m => (
                          <label key={m} className="flex items-center gap-2 cursor-pointer select-none">
                            <input type="radio" name="collection_mode" value={m}
                              checked={collectionMode === m} onChange={() => setCollectionMode(m)}
                              disabled={submitting} className="accent-primary" />
                            <span className="text-sm text-gray-700 capitalize">{m}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </fieldset>
                </>
              )}

              {/* Microsoft Graph — auth first, then folder path */}
              {sourceKind === 'graph' && (
                <>
                  <fieldset className="field-group">
                    <legend className="field-group-legend">Authentication</legend>
                    <div className="field-group-body">
                      {sourceToEdit ? (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          Microsoft account already linked to this source.
                        </div>
                      ) : graphConnected ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--green-700, #15803d)' }}>
                          <CheckCircle2 size={13} />
                          Connected — enter the folder path below and click Submit.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <Button
                            variant="outline"
                            type="button"
                            disabled={submitting || graphConnecting}
                            onClick={handleGraphConnectInForm}
                            style={{ alignSelf: 'flex-start' }}
                          >
                            {graphConnecting ? 'Connecting…' : pendingGraphSource ? 'Retry connection' : 'Connect Microsoft Account'}
                          </Button>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                            A login popup will open so you can authorise AnyGRC to read your OneDrive / SharePoint files.
                          </div>
                        </div>
                      )}
                    </div>
                  </fieldset>
                  <FormControl.Input
                    label="OneDrive / SharePoint folder path" required
                    value={graphFolder}
                    onChange={e => setGraphFolder(e.target.value)}
                    placeholder="/Finance/Sales Data  or  /sites/Finance/Shared Documents/Reports"
                    disabled={submitting || (!sourceToEdit && !graphConnected)}
                  />
                </>
              )}

              {/* Combined dataset — full join configuration */}
              {sourceKind === 'combined' && (
                <CombineDataPanel
                  testidPrefix="cm_combined"
                  availableSources={allSources}
                  config={combineConfig}
                  busy={submitting}
                  isLocked={false}
                  onChange={patchCombine}
                  onJoinKeyChange={patchJoinKey}
                  onAddJoinKey={() => setCombineConfig(prev => ({ ...prev, joinKeys: [...prev.joinKeys, mkJoinKey()] }))}
                  onRemoveJoinKey={ki => setCombineConfig(prev => ({ ...prev, joinKeys: prev.joinKeys.filter((_, i) => i !== ki) }))}
                />
              )}

              {/* Aggregation — no form; Submit delegates to AggregationBuilderModal */}
              {sourceKind === 'aggregation' && (
                <div style={{
                  background: 'var(--bg-subtle, #f8fafc)', border: '1px dashed var(--border-color, #e2e8f0)',
                  borderRadius: 8, padding: '14px 16px', fontSize: 13, color: 'var(--text-muted)',
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                }}>
                  <BarChart3 size={18} style={{ marginTop: 1, flexShrink: 0, color: 'var(--primary, #4f46e5)' }} />
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-heading)', marginBottom: 4 }}>Aggregation Dataset</div>
                    <div style={{ lineHeight: 1.5 }}>
                      Click <strong>Open Builder</strong> to define the group-by columns, measure,
                      and base source. The aggregation source is created automatically once saved.
                    </div>
                  </div>
                </div>
              )}
              </>)} {/* end !cloneMode */}
            </>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        {isEdit && onDelete && (
          <Button variant="delete-soft" icon={Trash2} onClick={onDelete}
            disabled={submitting} style={{ marginRight: 'auto' }}
            data-testid="BTN_cm_pipeline_delete_modal">
            Delete
          </Button>
        )}
        <Button
          type="submit" form="cm-pipeline-form"
          variant={pipeline ? 'update' : 'fill'}
          icon={pipeline ? Check : Save}
          disabled={permLoading || !canModify || !canSubmit}
          title={!canModify ? NOT_AUTHORISED_MSG : undefined}
          data-testid="BTN_cm_pipeline_save"
        >
          {pipeline ? 'Update' : isDs && sourceKind === 'aggregation' ? 'Open Builder' : 'Submit'}
        </Button>
      </Modal.Footer>
      </Modal>
      <AuthenticatorModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSaved={rec => {
          setAuthenticators(prev => [rec, ...prev]);
          setAuthenticatorId(rec.id);
          setAuthModalOpen(false);
        }}
      />
    </>
  );
};

export default CmPipelineEditorModal;
